import Link from 'next/link';
import { MessageCircle, Send, Mail, MapPin, Phone } from 'lucide-react';
import { SiteLogo } from '@/components/ui/SiteLogo';
import { Button } from '@/components/ui/button';

export function Footer() {
  const whatsappNumber = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER;
  const telegramHandle = process.env.NEXT_PUBLIC_TELEGRAM_HANDLE;
  const contactEmail = process.env.NEXT_PUBLIC_CONTACT_EMAIL || 'support@cardonecapvest.com';

  return (
    <footer className="relative bg-gradient-to-b from-card via-card to-accent/20 border-t border-border/50">
      {/* Decorative top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />

      <div className="container mx-auto px-4 pt-16 pb-8">
        {/* Main footer content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand section */}
          <div className="space-y-5">
            <Link href="/" className="inline-block mb-3 hover:opacity-80 transition-opacity">
              <SiteLogo />
            </Link>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Your partner in finding the perfect property. We offer expert advice and a wide range of listings.
            </p>
            <Button asChild size="sm" className="mt-4">
              <Link href="/properties">View Properties</Link>
            </Button>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-base mb-5 text-foreground tracking-wide uppercase text-sm">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link href="/properties" className="text-muted-foreground hover:text-primary transition-colors text-sm inline-flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  All Properties
                </Link>
              </li>
              <li>
                <Link href="/agents" className="text-muted-foreground hover:text-primary transition-colors text-sm inline-flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  Our Agents
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-muted-foreground hover:text-primary transition-colors text-sm inline-flex items-center gap-2 group">
                  <span className="w-1 h-1 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-base mb-5 text-foreground tracking-wide uppercase text-sm">Contact Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                <span>123 Real Estate Ave, Suite 500<br />New York, NY 10001</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                <Mail className="h-4 w-4 text-primary flex-shrink-0" />
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </li>
            </ul>
          </div>

          {/* Social & Newsletter */}
          <div>
            <h3 className="font-bold text-base mb-5 text-foreground tracking-wide uppercase text-sm">Stay Connected</h3>
            <p className="text-muted-foreground text-sm mb-4">Contact us directly via WhatsApp or Telegram for quick support.</p>
            <div className="flex gap-3">
              {whatsappNumber && (
                <a
                  href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="h-10 w-10 rounded-full bg-accent hover:bg-green-500 hover:text-white transition-all flex items-center justify-center group"
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
              )}
              {telegramHandle && (
                <a
                  href={`https://t.me/${telegramHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Telegram"
                  className="h-10 w-10 rounded-full bg-accent hover:bg-blue-500 hover:text-white transition-all flex items-center justify-center group"
                >
                  <Send className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-border/50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Cardone Capvest. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link href="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link href="#" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
